import React, { useState, useMemo, useEffect, useContext, useRef } from 'react';
import { themeColors } from '../theme';
import PlotExportBar from './PlotExportBar';
import SimProgressBar from './SimProgressBar';
import { serializeLayer } from './SimulatorPanel';
import { runJob } from '../services/jobClient';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { WorkspaceContextSetter } from '../context/WorkspaceContext';

// axisStyle is built per-render inside EQEPanel via useTheme().

function makeCustomTooltip(c) {
  return function CustomTooltip({ active, payload, label }) {
    if (!active || !payload || !payload.length) return null;
    return (
      <div style={{
        background: c.bgCard, border: `1px solid ${c.border}`,
        padding: '6px 10px',
        fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: c.text,
      }}>
        <div style={{ color: c.textMuted }}>λ = {label} nm</div>
        {payload.map(p => (
          <div key={p.name} style={{ color: p.color }}>
            {p.name}: {(p.value * 100).toFixed(1)} %
          </div>
        ))}
      </div>
    );
  };
}

function deltaColor(absDelta, sim, c) {
  if (!sim || sim <= 0) return c.textMuted;
  const pct = Math.abs(absDelta) / sim * 100;
  if (pct < 5)  return c.green;
  if (pct < 10) return c.amber;
  return c.red;
}

export default function EQEPanel({ results, theme = 'dark', device = null, simSettings = null }) {
  const c = themeColors(theme);
  const colors = c;
  const CustomTooltip = makeCustomTooltip(c);
  const axisStyle = {
    fill: c.plotText, fontSize: 13,
    fontFamily: 'IBM Plex Mono, monospace',
  };
  const btnStyle = {
    padding: '8px 18px', fontSize: 13, fontFamily: 'IBM Plex Mono, monospace',
    background: c.btnPrimary, border: `1px solid ${c.btnPrimary}`,
    color: c.btnPrimaryText,
    borderRadius: 3, cursor: 'pointer', fontWeight: 500,
  };
  const btnDisabled = {
    ...btnStyle, background: c.btnInactive, borderColor: c.border,
    color: c.btnInactiveText, cursor: 'not-allowed',
  };
  const [sweeping, setSweeping] = useState(false);
  // Progress: REAL per-wavelength counts polled from the async job
  // endpoint (V1.0). Until the first poll returns, a local elapsed-time
  // estimate keeps the bar moving (instant fallback).
  const [eqeProgress, setEqeProgress] = useState(0);
  const [eqeTotal,    setEqeTotal]    = useState(0);
  const [realProgress, setRealProgress] = useState(null);
  const realSeen = useRef(false);
  const [spectrum, setSpectrum] = useState(null);
  const [jsc, setJsc] = useState(null);
  const [error, setError] = useState(null);
  const [warnings, setWarnings] = useState([]);        // backend warnings
  const [warningsDismissed, setWarningsDismissed] = useState(false);
  const [wlStart, setWlStart] = useState(300);
  const [wlEnd, setWlEnd] = useState(1100);
  const [wlStep, setWlStep] = useState(25);

  // ── Stage 2: publish to WorkspaceContext (eqe block) ──────────────
  const publish = useContext(WorkspaceContextSetter);
  useEffect(() => {
    publish('eqe', {
      wl_start_nm: wlStart,
      wl_end_nm: wlEnd,
      wl_step_nm: wlStep,
      sweeping,
      progress: sweeping
        ? { done: eqeProgress, total: eqeTotal }
        : null,
      spectrum_points: Array.isArray(spectrum) ? spectrum.length : 0,
      spectrum_inline: null,  // Stage 3 enforces < 200-point inline rule
      jsc_eqe_mA: jsc?.jsc_eqe_mA ?? null,
      warnings,
      error,
    });
  }, [publish, wlStart, wlEnd, wlStep, sweeping, eqeProgress, eqeTotal,
      spectrum, jsc, warnings, error]);

  const hasDevice = !!(results && results._lastPayload);

  // v1.1.0 — EQE MUST follow the JV: it reuses the JV's submitted device
  // AND physics options (jv_mode is irrelevant for the V=0 EQE solves,
  // but chi_transport / robin / WG-series / optics carry over on the
  // backend). If the device in Define Device was edited after the JV
  // ran, the EQE would silently describe a different device — detect
  // that and ask for a fresh JV first.
  const lastPayload = results?._lastPayload || null;
  const deviceStale = useMemo(() => {
    if (!lastPayload || !device) return false;
    try {
      const cur = JSON.stringify({
        f: (device.frontContact?.layers || []).map(serializeLayer),
        a: (device.absorber?.layers || []).map(serializeLayer),
        b: (device.backContact?.layers || []).map(serializeLayer),
        fi: device.frontInterface || {}, bi: device.backInterface || {},
        c: device.contacts || {},
      });
      const ran = JSON.stringify({
        f: lastPayload.front_contact_layers || [],
        a: lastPayload.absorber_layers || [],
        b: lastPayload.back_contact_layers || [],
        fi: lastPayload.front_interface || {}, bi: lastPayload.back_interface || {},
        c: {
          front_WF: lastPayload.front_contact?.work_function, front_Sn: lastPayload.front_contact?.Sn,
          front_Sp: lastPayload.front_contact?.Sp, back_WF: lastPayload.back_contact?.work_function,
          back_Sn: lastPayload.back_contact?.Sn, back_Sp: lastPayload.back_contact?.Sp,
        },
      });
      return cur !== ran;
    } catch { return false; }
  }, [lastPayload, device]);
  const physicsStale = useMemo(() => {
    if (!lastPayload || !simSettings) return false;
    const want = {
      optics_model: simSettings.optics_model || 'beer_lambert',
      chi_transport: !!simSettings.chi_transport,
      contact_bc: simSettings.contact_bc || 'pinned',
      wg_series_resistance: !!simSettings.wg_series_resistance,
    };
    const ran = {
      optics_model: lastPayload.optics_model || 'beer_lambert',
      chi_transport: !!lastPayload.chi_transport,
      contact_bc: lastPayload.contact_bc || 'pinned',
      wg_series_resistance: !!lastPayload.wg_series_resistance,
    };
    return JSON.stringify(want) !== JSON.stringify(ran);
  }, [lastPayload, simSettings]);
  const stale = deviceStale || physicsStale;
  const canRun = hasDevice && !stale;

  // Primary absorber Eg = thickest layer with 0.9 < Eg < 2.0 in absorber_layers
  // (fall back to flat layers, then 1.50 eV CdTe default)
  const primaryEg = useMemo(() => {
    const p = results?._lastPayload;
    if (!p) return 1.50;
    const candidates = (p.absorber_layers && p.absorber_layers.length
                         ? p.absorber_layers
                         : (p.layers || []))
      .filter(l => l.Eg != null && l.Eg > 0.9 && l.Eg < 2.0);
    if (!candidates.length) return 1.50;
    return candidates.reduce((best, l) =>
      (l.thickness_um || 0) > (best.thickness_um || 0) ? l : best
    ).Eg;
  }, [results]);
  const bandgapNm = 1240 / primaryEg;

  const chartData = useMemo(
    () => (spectrum || []).map(p => ({
      wavelength_nm: p.wavelength_nm,
      EQE: p.eqe,
    })),
    [spectrum]);

  const nWavelengths = Math.max(
    0, Math.floor((wlEnd - wlStart) / wlStep) + 1);
  const EST_SEC_PER_POINT = 0.6;    // ~0.5–0.6 s per solve point
  const estSeconds = Math.ceil(nWavelengths * EST_SEC_PER_POINT);

  async function handleRun() {
    if (!canRun) return;
    setSweeping(true);
    setEqeTotal(nWavelengths);
    setEqeProgress(0);
    setRealProgress(null);
    realSeen.current = false;
    setError(null);
    setWarnings([]);
    setWarningsDismissed(false);
    setSpectrum(null);
    setJsc(null);
    const t0 = Date.now();
    // Estimate pump (feeds the WorkspaceContext 'eqe' slice): keeps the
    // count moving until the FIRST job poll returns real progress, then
    // the polled per-wavelength numbers take over.
    const cap = Math.max(1, Math.floor(nWavelengths * 0.9));
    const pump = setInterval(() => {
      if (realSeen.current) return;
      const elapsed = (Date.now() - t0) / 1000;
      const est = Math.min(cap, Math.floor(elapsed / EST_SEC_PER_POINT));
      setEqeProgress(est);
    }, 200);
    try {
      const payload = { ...results._lastPayload,
        wl_start_nm: wlStart, wl_end_nm: wlEnd, wl_step_nm: wlStep };
      // Async job flow (V1.0): enqueue + poll — survives HTTP timeouts
      // on long sweeps and reports REAL per-wavelength progress. The
      // result body matches the old synchronous /simulation/eqe response.
      const data = await runJob('eqe', payload, {
        onProgress: (p) => {
          realSeen.current = true;
          setRealProgress(p);
          if (p.total > 0) {
            setEqeTotal(p.total);
            setEqeProgress(Math.min(p.total, p.current));
          }
        },
      });
      if (data && data.success === false) {
        setError(data.error || 'EQE sweep failed');
      } else {
        setSpectrum(data.spectrum || []);
        setJsc({
          simulated: data.jsc_simulated_mA,
          eqe:       data.jsc_eqe_mA,
          delta:     data.jsc_delta_mA,
        });
        setWarnings(Array.isArray(data.warnings) ? data.warnings : []);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Network error');
    } finally {
      clearInterval(pump);
      setEqeProgress(nWavelengths);
      setSweeping(false);
    }
  }

  const inputStyle = {
    marginLeft: 6, width: 70, padding: '4px 6px',
    background: c.input, border: `1px solid ${c.border}`,
    color: c.inputText, fontSize: 13,
  };
  const labelStyle = {
    fontSize: 13, color: c.textMuted,
    fontFamily: 'IBM Plex Mono, monospace',
  };

  return (
    <div style={{ padding: 20, color: c.text, background: c.bg, minHeight: '100%' }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontFamily: 'IBM Plex Mono, monospace',
                      fontSize: 16, color: c.accentDark }}>
          EQE Spectrum &amp; Jsc Cross-Validation
        </h2>
        <div style={{ fontSize: 13, color: c.textMuted, marginTop: 4, lineHeight: 1.5 }}>
          Runs one short-circuit solve per wavelength with a monochromatic
          Beer-Lambert G(x), then integrates against AM1.5G to compare against
          the broadband Jsc.
        </div>
      </div>

      {!hasDevice && (
        <div style={{ padding: 20, background: c.bgSecondary,
                       border: `1px solid ${c.border}`,
                       color: c.textMuted, fontSize: 13 }}>
          Run the JV simulation first — the EQE follows the JV run: same
          device, same optics and solver-physics options.
        </div>
      )}

      {hasDevice && stale && (
        <div style={{ padding: '10px 14px', marginBottom: 12,
                       border: `1px solid ${c.amber || '#f0a500'}`, borderRadius: 4,
                       color: c.amber || '#f0a500', fontSize: 13, lineHeight: 1.5 }}>
          ⚠ {deviceStale
            ? 'The device was edited after the last JV run.'
            : 'The solver-physics / optics settings changed after the last JV run.'}{' '}
          Re-run the JV simulation on the Simulation tab first — the EQE must
          follow the JV (same device and physics), otherwise the two would
          describe different cells.
        </div>
      )}

      {hasDevice && !stale && (
        <div style={{ fontSize: 11, color: c.textDim, marginBottom: 8,
                       fontFamily: 'IBM Plex Mono, monospace' }}>
          Follows the last JV run
          {results?.jv_method ? ` (${results.jv_method} JV` : ' ('}
          {lastPayload?.optics_model === 'tmm' ? ', TMM optics' : ', Beer-Lambert optics'}
          {lastPayload?.chi_transport ? ', χ-transport' : ''}
          {lastPayload?.contact_bc === 'robin' ? ', Robin contacts' : ''}
          {')'} — Jsc from the EQE integral is cross-checked against the JV Jsc below.
        </div>
      )}

      {hasDevice && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap',
                       alignItems: 'center', marginBottom: 16 }}>
          <label style={labelStyle}>
            λ start (nm):
            <input type="number" value={wlStart} min={200} max={1500}
                   onChange={e => setWlStart(Number(e.target.value))}
                   style={inputStyle} />
          </label>
          <label style={labelStyle}>
            λ end (nm):
            <input type="number" value={wlEnd} min={300} max={2000}
                   onChange={e => setWlEnd(Number(e.target.value))}
                   style={inputStyle} />
          </label>
          <label style={labelStyle}>
            step (nm):
            <input type="number" value={wlStep} min={5} max={100}
                   onChange={e => setWlStep(Number(e.target.value))}
                   style={{ ...inputStyle, width: 60 }} />
          </label>
          <div style={{ fontSize: 13, color: c.text,
                         fontFamily: 'IBM Plex Mono, monospace',
                         padding: '4px 8px', border: `1px solid ${c.border}`,
                         background: c.bgSecondary, borderRadius: 3 }}>
            Estimated time: ~{estSeconds}s ({nWavelengths} wavelength points)
          </div>
          <button
            style={sweeping || !canRun ? btnDisabled : btnStyle}
            disabled={sweeping || !canRun}
            title={stale ? 'Re-run the JV first — the EQE follows the JV device and physics' : undefined}
            onClick={handleRun}
          >
            {sweeping ? 'Calculating…' : 'Calculate EQE'}
          </button>
        </div>
      )}

      {/* Progress — one SC solve per wavelength. REAL counts polled from
          the job endpoint once available; time-estimated until then.
          SimProgressBar owns its timer; the pump above still feeds the
          WorkspaceContext counts before the first poll. */}
      <SimProgressBar
        active={sweeping}
        totalPoints={nWavelengths}
        perPointSec={EST_SEC_PER_POINT}
        label="Calculating EQE"
        c={c}
        progress={realProgress}
      />

      {error && (
        <div style={{ padding: 12, border: `1px solid ${c.red}`, color: c.red,
                       background: theme === 'dark'
                         ? 'rgba(248,113,113,0.05)' : 'rgba(220,53,69,0.08)',
                       fontSize: 13,
                       fontFamily: 'IBM Plex Mono, monospace', marginBottom: 16 }}>
          {error}
        </div>
      )}

      {warnings.length > 0 && !warningsDismissed && (
        <div style={{ padding: 12, border: `1px solid ${c.amber}`,
                       color: c.amber,
                       background: theme === 'dark'
                         ? 'rgba(251,191,36,0.05)' : 'rgba(255,193,7,0.08)',
                       fontSize: 13,
                       fontFamily: 'IBM Plex Mono, monospace',
                       marginBottom: 16, display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              Backend warnings ({warnings.length})
            </div>
            {warnings.map((w, i) => (
              <div key={i} style={{ marginTop: 2 }}>• {w}</div>
            ))}
          </div>
          <button onClick={() => setWarningsDismissed(true)}
                  title="Dismiss warnings"
                  style={{ background: 'none', border: 'none', color: c.amber,
                            cursor: 'pointer', fontSize: 16, padding: '0 4px',
                            alignSelf: 'flex-start' }}>
            ×
          </button>
        </div>
      )}

      {jsc && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                       gap: 12, marginBottom: 16 }}>
          <div style={{ padding: 12, background: c.bgSecondary,
                         border: `1px solid ${c.border}` }}>
            <div style={{ fontSize: 11, color: c.textMuted,
                           fontFamily: 'IBM Plex Mono, monospace' }}>
              Jsc (simulated, AM1.5G)
            </div>
            <div style={{ fontSize: 20, color: c.text,
                           fontFamily: 'IBM Plex Mono, monospace' }}>
              {jsc.simulated.toFixed(2)} mA/cm²
            </div>
          </div>
          <div style={{ padding: 12, background: c.bgSecondary,
                         border: `1px solid ${c.border}` }}>
            <div style={{ fontSize: 11, color: c.textMuted,
                           fontFamily: 'IBM Plex Mono, monospace' }}>
              Jsc (from EQE · Φ_AM1.5G)
            </div>
            <div style={{ fontSize: 20, color: c.text,
                           fontFamily: 'IBM Plex Mono, monospace' }}>
              {jsc.eqe.toFixed(2)} mA/cm²
            </div>
          </div>
          <div style={{ padding: 12, background: c.bgSecondary,
                         border: `1px solid ${deltaColor(jsc.delta, jsc.simulated, c)}` }}>
            <div style={{ fontSize: 11, color: c.textMuted,
                           fontFamily: 'IBM Plex Mono, monospace' }}>
              Δ (eqe − sim)
            </div>
            <div style={{ fontSize: 20,
                           color: deltaColor(jsc.delta, jsc.simulated, c),
                           fontFamily: 'IBM Plex Mono, monospace' }}>
              {jsc.delta >= 0 ? '+' : ''}{jsc.delta.toFixed(2)} mA/cm²
              {jsc.simulated > 0 && (
                <span style={{ fontSize: 13, marginLeft: 8 }}>
                  ({(Math.abs(jsc.delta) / jsc.simulated * 100).toFixed(1)}%)
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {spectrum && spectrum.length > 0 && (
        <div id="eqe-chart-container" style={{
          background: c.plotBg, border: `1px solid ${c.border}`,
          padding: 12, width: '100%',
          height: 440, minHeight: 350, borderRadius: 6,
        }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}
                        margin={{ top: 24, right: 60, bottom: 40, left: 40 }}>
              <CartesianGrid stroke={colors.plotGrid} strokeDasharray="2 4" />
              <XAxis
                dataKey="wavelength_nm"
                type="number"
                domain={[wlStart, wlEnd]}
                tick={axisStyle}
                label={{ value: 'Wavelength (nm)', position: 'insideBottom',
                          offset: -10, ...axisStyle }}
              />
              <YAxis
                domain={[0, 1]}
                tick={axisStyle}
                label={{ value: 'EQE', angle: -90, position: 'insideLeft',
                          offset: -4, textAnchor: 'middle', ...axisStyle }}
              />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine x={bandgapNm} stroke={colors.red}
                              strokeDasharray="4 4"
                              label={{
                                value: `Eg = ${primaryEg.toFixed(2)} eV (${bandgapNm.toFixed(0)} nm)`,
                                position: 'top',
                                fill: '#f87171',
                                fontSize: 10,
                              }} />
              <Line type="monotone" dataKey="EQE"
                    stroke={colors.accent} strokeWidth={2}
                    dot={{ r: 3, fill: '#00d4ff' }} name="EQE" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {spectrum && spectrum.length > 0 && (
        <PlotExportBar
          chartId="eqe-chart-container"
          csvData={spectrum.map(p => ({ wavelength_nm: p.wavelength_nm, eqe: p.eqe }))}
          csvFilename="jvista_eqe" plotFilename="eqe"
          c={c}
          delayMs={100} />
      )}
    </div>
  );
}
